import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import smileRingtoneUri from "../../assets/audio/smile-ringtone.mp3?url";
import { gracefullyPlayAudio } from "../../audio";
import { NOTIFICATION_VOLUME } from "../../config";
import { logger } from "../../datadog/logger";
import { useGetTelehealthSessionInfoCallback } from "../../hooks";
import { twilioSyncClientAtom } from "../../session";
import { delayedEntryGate } from "../../statsig/gates";
import { Statsig } from "../../statsig/StatsigProvider";
import { debounce, withRetry } from "../../utils";
import { roomTokenAtom } from "../../video-sdk";
import { zoomTokenAtom } from "../../video-sdk/zoom";
import {
  apiTokenAtom,
  preSessionVibesOpenAtom,
  sessionAppointmentShortIdAtom,
  visitorAccessDocSidAtom,
  VisitorState,
  visitorStateAtom,
} from "../state";
import { useVisitorId } from "../useVisitorId";
import {
  deadlineAtom,
  sessionConversationSidAtom,
} from "./delayed-entry/state";
import { useVisitorConnectRoomCallback } from "./admission/useVisitorConnectRoomCallback";
import { useAtomCallback } from "jotai/utils";
import {
  admissionAttemptsAtom,
  admissionTimeAtom,
  isAdmissionModalOpenAtom,
} from "./admission/state";
import {
  Modal,
  useOpenVisitorModalCallback,
} from "../modals/useOpenVisitorModalCallback";
import { setAlertTitle } from "../../metadata";
import { ADMITTED_TITLE } from "./admission/metadata";
import { useUpdateVisitorPresenceCallback } from "./useUpdateVisitorPresenceCallback";
import { useAdmissionFlowCallback } from "../../hooks/useAdmissionFlow";
import { DebugMutex } from "../../video-sdk/core/mutex";

const LOAD_TIME_MS = 1000;
const DEBOUNCE_TIME_MS = 1000;

enum VisitorAccessDocEventType {
  GRANTED = "access-granted",
  DENIED = "access-denied",
}

type VisitorAccessDocData = {
  eventType: VisitorAccessDocEventType;
  token: string;
  /**
   * A token that can be used to communicate with the Telehealth API as a client
   * user
   */
  apiToken?: string;
  conversationSid?: string;
  deadline?: string;
  zoomJwt: string;
  admissionTime?: string;
};

const playJoiningRoomSound = debounce(() => {
  const ringtone = new Audio(smileRingtoneUri);
  ringtone.volume = NOTIFICATION_VOLUME;
  gracefullyPlayAudio(ringtone);
}, DEBOUNCE_TIME_MS);

type HookOptions = {
  onDone?: () => void;
};

/**
 * Gracefully fetches the session appointment short ID and sets it in Jotai
 */
function useSetSessionAppointmentShortIdCallback() {
  const { providerShortId } = useParams<{ providerShortId: string }>();
  const { patientShortId } = useParams<{ patientShortId: string }>();
  const setSessionAppointmentShortId = useSetAtom(
    sessionAppointmentShortIdAtom,
  );
  const [getTelehealthSessionInfo] = useGetTelehealthSessionInfoCallback();

  return useCallback(async () => {
    try {
      const { data } = await getTelehealthSessionInfo({
        variables: {
          patientShortId,
          providerShortId,
        },
      });
      setSessionAppointmentShortId(
        data?.telehealthSessionInfo?.appointment?.shortId,
      );
    } catch (error) {
      logger.error(
        "Failed to fetch/set appointment short ID for session",
        {
          patientShortId,
          providerShortId,
        },
        error as Error,
      );
    }
  }, [
    getTelehealthSessionInfo,
    patientShortId,
    providerShortId,
    setSessionAppointmentShortId,
  ]);
}

const roomMutex = new DebugMutex("roomMutex");
export function useTransitionToMeetingRoom() {
  const setVisitorStateAtom = useSetAtom(visitorStateAtom);
  const connectRoom = useVisitorConnectRoomCallback();

  return useCallback(
    ({ onDone }: HookOptions = {}) => {
      // if multiple calls to transition to the meeting room happen simulatenously
      // the first one "wins" and the rest can just no-op.
      if (roomMutex.isLocked()) return;

      roomMutex.runExclusive(async () => {
        playJoiningRoomSound();
        setVisitorStateAtom(VisitorState.LOADING);

        // effectively sleep, need to await this way so that setTimeout runs async
        // otherwise, fire-and-forget will release the mutex before this timeout has elapsed!
        await new Promise((resolve) => setTimeout(resolve, LOAD_TIME_MS));

        const room = await connectRoom();

        if (!room) {
          setVisitorStateAtom(VisitorState.WAITING);
          return;
        }

        onDone?.();
      }, "useTransitionToMeetingRoom");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connectRoom],
  );
}

export function useWatchForRoomToken() {
  const visitorUuid = useVisitorId();
  const syncClient = useAtomValue(twilioSyncClientAtom);
  const visitorAccessDocSid = useAtomValue(visitorAccessDocSidAtom);
  const setDeadline = useSetAtom(deadlineAtom);
  const transitionToMeetingRoom = useTransitionToMeetingRoom();
  const setRoomToken = useSetAtom(roomTokenAtom);
  const setZoomToken = useSetAtom(zoomTokenAtom);
  const setApiToken = useSetAtom(apiTokenAtom);
  const setSessionConversationSid = useSetAtom(sessionConversationSidAtom);
  const setSessionAppointmentShortId =
    useSetSessionAppointmentShortIdCallback();
  const setIsAdmissionModalOpen = useSetAtom(isAdmissionModalOpenAtom);
  const getIsAdmissionModalOpen = useAtomCallback(
    useCallback((get) => get(isAdmissionModalOpenAtom), []),
  );
  const setAdmissionAttempts = useSetAtom(admissionAttemptsAtom);
  const openVisitorModal = useOpenVisitorModalCallback();
  const setAdmissionTime = useSetAtom(admissionTimeAtom);
  const updateVisitorPresence = useUpdateVisitorPresenceCallback();
  const setPreSessionVibesOpen = useSetAtom(preSessionVibesOpenAtom);
  const getAdmissionFlow = useAdmissionFlowCallback();
  const getIsJoiningRoom = useAtomCallback(
    useCallback((get) => get(visitorStateAtom) === VisitorState.JOINING, []),
  );

  useEffect(
    function watchForRoomToken() {
      (async () => {
        if (!syncClient) return;
        logger.info("Initializing visitor-access document", {
          docSid: visitorAccessDocSid,
        });
        const visitorAccessDoc = await syncClient.document(visitorAccessDocSid);
        logger.info("Initialized visitor-access document", {
          docSid: visitorAccessDocSid,
        });
        visitorAccessDoc.on(
          "updated",
          ({
            data: {
              eventType,
              token,
              apiToken,
              conversationSid,
              deadline,
              zoomJwt,
              admissionTime,
            },
          }: {
            data: VisitorAccessDocData;
          }) => {
            logger.info("Received visitor-access document update", {
              hasToken: !!token,
              eventType,
              conversationSid,
              deadline,
              admissionTime,
            });
            if (deadline) {
              setDeadline(new Date(deadline));
            }
            setRoomToken(token);
            setZoomToken(zoomJwt);
            setApiToken(apiToken);
            setSessionConversationSid(conversationSid);

            switch (eventType) {
              case VisitorAccessDocEventType.GRANTED: {
                // This is not session blocking so fire and forget
                setSessionAppointmentShortId();
                setAdmissionAttempts((attempts) => attempts + 1);

                switch (getAdmissionFlow()) {
                  // Feature gated to Zoom rn, but will prolly expand to Twilio
                  // too
                  case "manual":
                    if (admissionTime) {
                      // Important to relay the admission time back for the
                      // provider side to properly wait for the client to join
                      setAdmissionTime(admissionTime);
                      withRetry(() => updateVisitorPresence(), {
                        retries: 2,
                        initialDelayMs: 500,
                      });
                    }

                    setPreSessionVibesOpen(false);
                    if (!getIsAdmissionModalOpen() && !getIsJoiningRoom()) {
                      playJoiningRoomSound();
                      setAlertTitle(ADMITTED_TITLE);
                      openVisitorModal(Modal.ADMISSION);
                    }
                    break;
                  case "auto":
                  default:
                    if (!Statsig.checkGate(delayedEntryGate)) {
                      transitionToMeetingRoom();
                    }
                }
                return;
              }
              case VisitorAccessDocEventType.DENIED:
                // TODO: Handle denied event type
                return;
              default:
                logger.error("Received an unknown visitor-access event type", {
                  eventType,
                  visitorAccessDocSid,
                });
                return;
            }
          },
        );
      })();
    },
    [
      visitorUuid,
      syncClient,
      visitorAccessDocSid,
      transitionToMeetingRoom,
      setDeadline,
      setSessionConversationSid,
      setRoomToken,
      setApiToken,
      setSessionAppointmentShortId,
      setZoomToken,
      setIsAdmissionModalOpen,
      getIsAdmissionModalOpen,
      setAdmissionAttempts,
      openVisitorModal,
      setAdmissionTime,
      updateVisitorPresence,
      setPreSessionVibesOpen,
      getAdmissionFlow,
      getIsJoiningRoom,
    ],
  );
}
